/**
 * Pure helper functions for deriving My Account state from raw API responses.
 *
 * Extracted from useMyAccountData so they can be unit-tested independently
 * without needing React hooks or providers.
 */

import type { AccountData, BillingData, CurrentUser, UsageData } from './useMyAccountData.types';
import { EMPTY_ACCOUNT } from './useMyAccountData.types';

// ── deriveAccountData ───────────────────────────────────────────────────────

/**
 * Maps raw MyAccount + Dashboard API responses into an `AccountData` object.
 *
 * Priority: MyAccount organization → Dashboard organizationName → EMPTY_ACCOUNT
 */
export function deriveAccountData(
	myAccData: any,
	dashData: any,
	cognitoUser: any,
): AccountData {
	if (myAccData?.organization) {
		const org = myAccData.organization;
		return {
			organisation: org.partyName || '',
			abn: org.partyAbn || '',
			acn: org.partyAcn || undefined,
			accountId: org.organizationId || '',
			sector: org.reportingEntityType || org.industryDescription || org.partySector || org.services || '',
			services: org.industryDescription || org.services || '',
			adminName: org.contactPerson || '',
			adminEmail: org.contactEmail || '',
			adminPhone: org.phone || '',
			entityType: (org.partyType as AccountData['entityType']) || 'company',
			reportingEntityType: org.reportingEntityType || undefined,
			address: org.businessAddress || null,
		};
	}
	if (dashData?.organizationName) {
		return {
			...EMPTY_ACCOUNT,
			organisation: dashData.organizationName,
			adminEmail: cognitoUser?.email || '',
			adminName: cognitoUser?.name || '',
		};
	}
	return EMPTY_ACCOUNT;
}

// ── deriveCurrentUser ───────────────────────────────────────────────────────

/**
 * Resolves the person holding the authenticated connection. The backend's
 * currentUser block is authoritative; Cognito is a compatibility fallback for
 * environments where AL-363 has not deployed yet.
 */
export function deriveCurrentUser(myAccData: any, cognitoUser: any): CurrentUser {
	const serverUser = myAccData?.currentUser;
	const firstName = serverUser?.firstName?.trim() || undefined;
	const lastName = serverUser?.lastName?.trim() || undefined;
	const serverName = [firstName, lastName].filter(Boolean).join(' ');
	const email = serverUser?.email || cognitoUser?.email || '';

	return {
		userId: serverUser?.userId || cognitoUser?.id || undefined,
		email,
		firstName,
		lastName,
		role: serverUser?.role || undefined,
		displayName: serverUser
			? serverName || email || 'User'
			: cognitoUser?.name?.trim() || email || 'User',
	};
}

// ── deriveBillingData ───────────────────────────────────────────────────────

/**
 * Derives `BillingData` from subscription info in MyAccount or Dashboard.
 * Returns `null` if no subscription is found.
 */
export function deriveBillingData(myAccData: any, dashData: any): BillingData | null {
	const sub = myAccData?.subscription || dashData?.subscription;
	if (!sub) return null;
	return {
		stripePlanType: sub.planType || 'one-time',
		billingCycle: sub.billingInterval === 'annual' ? 'annual' : 'monthly',
		paymentMethod: 'card',
		startDate: sub.currentPeriodStart,
	};
}

// ── deriveSelectedPlan ──────────────────────────────────────────────────────

/**
 * Picks the plan type string from the subscription in either MyAccount or
 * Dashboard response. Falls back to `'GROWTH'`.
 */
export function deriveSelectedPlan(myAccData: any, dashData: any): string {
	const sub = myAccData?.subscription || dashData?.subscription;
	return sub?.planType || 'GROWTH';
}

// ── deriveUsageData ─────────────────────────────────────────────────────────

/**
 * Builds the `UsageData` object by merging data from three sources
 * (in ascending priority): subscription/credits → dashboard → ledger.
 *
 * @param now - injectable `Date` for deterministic testing of month/year logic.
 */
export function deriveUsageData(
	myAccData: any,
	dashData: any,
	ledgerResult: { transactions: any[]; credits: any } | null,
	storeInvitations: Map<string, any>,
	now: Date = new Date(),
): UsageData | null {
	const sub = myAccData?.subscription;
	const cred = myAccData?.credits;
	const dash = dashData;

	// Start with subscription/dashboard-level credits
	let creditsTotal = sub?.credits_total ?? sub?.creditsTotal ?? cred?.creditsTotal ?? cred?.lifetimePurchased
		?? dash?.creditsTotal ?? dash?.subscription?.credits_total ?? dash?.subscription?.creditsTotal ?? 0;
	let creditsUsed = sub?.credits_used ?? sub?.creditsUsed ?? cred?.creditsUsed ?? cred?.lifetimeUsed
		?? dash?.creditsUsed ?? dash?.subscription?.credits_used ?? dash?.subscription?.creditsUsed ?? 0;
	let creditsRemaining = dash?.creditsRemaining ?? cred?.creditsRemaining ?? cred?.balance ?? (creditsTotal - creditsUsed);
	let creditsReserved = cred?.reserved;

	// Ledger-derived monthly/yearly counts
	const currentMonth = now.getMonth();
	const currentYear = now.getFullYear();
	const txns = ledgerResult?.transactions || [];
	const debitTxns = txns.filter((t) => t.amount < 0);
	const derivedThisMonth = debitTxns.filter((t) => {
		const d = new Date(t.processedAt || t.createdAt);
		return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
	}).length;
	const derivedThisYear = debitTxns.filter((t) => {
		const d = new Date(t.processedAt || t.createdAt);
		return d.getFullYear() === currentYear;
	}).length;

	// These are chargeable-event windows, not case totals. Never substitute the
	// dashboard's unwindowed totalRecords value: it is a different population and
	// made both periods identical as soon as it became non-zero (AL-358).
	let cddsThisMonth = derivedThisMonth;
	let cddsYearToDate = derivedThisYear;

	// Ledger credits override (authoritative real-time balance)
	if (ledgerResult?.credits) {
		const lc = ledgerResult.credits;
		creditsTotal = lc.creditsTotal ?? lc.lifetimePurchased ?? creditsTotal;
		creditsUsed = lc.creditsUsed ?? lc.lifetimeUsed ?? creditsUsed;
		creditsRemaining = lc.creditsRemaining ?? lc.balance ?? creditsRemaining;
		cddsThisMonth = lc.usedThisMonth ?? lc.monthlyUsed ?? lc.creditsUsedThisMonth ?? derivedThisMonth;
		cddsYearToDate = lc.usedThisYear ?? lc.yearlyUsed ?? lc.creditsUsedThisYear ?? derivedThisYear;
	}

	if (!sub && !cred && !dash) return null;

	return {
		creditsTotal,
		creditsUsed,
		creditsRemaining,
		creditsReserved,
		cddsThisMonth,
		cddsYearToDate,
		totalInvitations: dash?.totalInvitations ?? storeInvitations.size,
		pendingInvitations: dash?.pendingInvitations ?? [...storeInvitations.values()].filter(i => i.status === 'pending').length,
		acceptedInvitations: dash?.acceptedInvitations ?? [...storeInvitations.values()].filter(i => i.status === 'accepted').length,
		completed: {
			count: dash?.completedRecords || 0,
			percentage: dash?.totalRecords
				? Math.round((dash.completedRecords / dash.totalRecords) * 100)
				: 0,
		},
		inProgress: {
			count: dash?.pendingRecords || 0,
			percentage: dash?.totalRecords
				? Math.round((dash.pendingRecords / dash.totalRecords) * 100)
				: 0,
		},
		failed: {
			count: dash?.failedRecords || 0,
			percentage: dash?.totalRecords
				? Math.round((dash.failedRecords / dash.totalRecords) * 100)
				: 0,
		},
	};
}

// ── mergeCreditOverride ─────────────────────────────────────────────────────

/**
 * Merges a real-time credit notification override into usage data.
 * Returns the original if no override is present.
 */
export function mergeCreditOverride(
	usageData: UsageData | null,
	creditOverride: number | null,
): UsageData | null {
	if (usageData && creditOverride != null) {
		return {
			...usageData,
			// A notification carries the current balance only. Preserve the
			// lifetime-purchased denominator supplied by the ledger (AL-358).
			creditsRemaining: creditOverride,
		};
	}
	return usageData;
}
